"use client"

import React from 'react'
import { UIButton, type UIButtonProps } from './ui-button'
import { cn } from '../../lib/utils'

export interface UIButtonToggleProps extends Omit<UIButtonProps, 'onClick'> {
  pressed?: boolean
  defaultPressed?: boolean
  onPressedChange?: (pressed: boolean) => void
  pressedVariant?: UIButtonProps['variant']
  unpressedVariant?: UIButtonProps['variant']
}

/**
 * Toggle Button Component
 * Based on toggle button patterns for filters, settings
 * Use for toggleable states like favorites, filters, selections
 */
export const UIButtonToggle = React.forwardRef<HTMLButtonElement, UIButtonToggleProps>(
  ({
    pressed: controlledPressed,
    defaultPressed = false,
    onPressedChange,
    pressedVariant = 'default',
    unpressedVariant = 'outline',
    className,
    children,
    ...props
  }, ref) => {
    const [internalPressed, setInternalPressed] = React.useState(defaultPressed)

    const isControlled = controlledPressed !== undefined
    const pressed = isControlled ? controlledPressed : internalPressed

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      const newPressed = !pressed

      if (!isControlled) {
        setInternalPressed(newPressed)
      }

      onPressedChange?.(newPressed)
    }

    const currentVariant = pressed ? pressedVariant : unpressedVariant

    return (
      <UIButton
        ref={ref}
        variant={currentVariant}
        onClick={handleClick}
        aria-pressed={pressed}
        className={cn(
          "transition-[transform,background-color,color,border-color,box-shadow]",
          pressed && "ring-1 ring-accent/60 ring-offset-1",
          className
        )}
        {...props}
      >
        {children}
      </UIButton>
    )
  }
)

UIButtonToggle.displayName = "UIButtonToggle"